// Pure helpers about what the broadcasts play: which kind a broadcast is, the final playlist of
// a composite broadcast, where a content item is used, and adding / removing one content item
// to / from several regular broadcasts. Nothing here touches Firebase: callers write the result
// with `updateMany` (lib/firebase.js) as ONE atomic update.
//
// (`lib/broadcast.js` holds the duration rules and has a CommonJS copy for the Cloud Functions;
// these helpers are only needed by the admin app, so they live apart from it.)

import { compareValues } from "lib/sort"
import { normalizeResolutions } from "lib/resolution"

// Composite or regular? `type` decides; a record without a type is composite when it has a list
// of broadcasts. A REGULAR broadcast may be empty (no `contentList` at all: the database does
// not keep empty lists), so the content list must never be what tells the two apart.
export const isCompositeBroadcast = (record) => !!record && (record.type === "composite" || Array.isArray(record.broadcastList))

// A regular broadcast without any content
export const hasNoContent = (record) => !!record && !isCompositeBroadcast(record) && !(record.contentList?.length > 0)

// The record the way it is saved for the chosen kind. The kind is written even when the list is
// empty (a regular broadcast with no content has no `contentList` in the database, and without
// a `type` it would be taken for a composite one). Resolutions belong to regular broadcasts only.
export const asKind = (record, kind) => kind === "composite"
  ? { ...record, type: "composite", broadcastList: record.broadcastList || [], contentList: null, resolutions: null }
  : { ...record, type: "regular", contentList: record.contentList || [], broadcastList: null, resolutions: normalizeResolutions(record.resolutions) }

// The order in which a device plays the clips of a composite broadcast. This is the formula of
// the Android player (BroadcastSorter), NOT "one broadcast after the other": the broadcasts are
// interleaved, the shorter a list, the wider its clips are spread.
//
//   N        = number of clips in all the broadcasts (a clip that is in a list twice counts twice)
//   i        = position of the broadcast in the list of the composite broadcast (from 0)
//   n_i      = number of clips of broadcast i
//   j        = position of the clip inside its broadcast (from 0)
//   step_i   = round(N / n_i, 2 decimals, HALF_UP)
//   index    = step_i * j + 1 + i
//
// All clips are sorted by `index`, ascending; clips with the same index keep the order they were
// put in (the one from the broadcast with the smaller i first). The result is played in turn and
// starts again from the beginning.
//
// The player works in decimal numbers (BigDecimal for the step). Here the same is done with whole
// HUNDREDTHS, so "the same index" is exact and there is no floating-point noise.
//
// `lists` = one array of clips per broadcast, in the order of the composite broadcast. A broadcast
// without clips takes no part, as if it was not in the list at all (i counts only the broadcasts
// that have clips). This is what the Android project's working copy does (BroadcastSorter), but
// the released player still divides by n_i: an empty list is a division by zero and it fails to
// synchronise. If a player skipped them but kept their place in i, the order would be another one.
// Returns the clips in playing order, each with its `position` (the index, e.g. 3.5).
export const compositeOrder = (lists) => {
  const filled = (lists || []).filter(clips => clips && clips.length > 0)
  const total = filled.reduce((sum, clips) => sum + clips.length, 0)
  const items = []
  filled.forEach((clips, i) => {
    // round(total / n, 2, HALF_UP) in hundredths: floor(100 * total / n + 1/2)
    const step = Math.floor((200 * total + clips.length) / (2 * clips.length))
    clips.forEach((clip, j) => {
      items.push({ hundredths: step * j + 100 * (1 + i), seq: items.length, clip })
    })
  })
  items.sort((a, b) => (a.hundredths - b.hundredths) || (a.seq - b.seq))
  return items.map(item => ({ ...item.clip, position: item.hundredths / 100 }))
}

// What a device plays for a list of broadcasts (the list of a composite broadcast), worked out
// with `compositeOrder`. A multiplier copy already holds its original's list repeated, so its
// clips are simply more clips of that broadcast.
// `existingContent` (optional, a Map or Set of content ids): clips whose content record no longer
// exists are left out BEFORE the order is worked out, the way the player does it (so they count
// in neither N nor n_i). Leave it out while the content list is still loading.
// Returns { steps, empty, missing, nested, deletedClips }:
//  - steps:   [{ contentId, broadcastId, position }] in playing order
//  - empty:   how many regular broadcasts of the list have no clips (they take no part). One whose
//             clips were all deleted counts here too.
//  - missing: how many ids of the list are broadcasts that no longer exist (also no part)
//  - nested:  how many broadcasts of the list are composite themselves. They have no `contentList`,
//             so the player (PlaylistResolver) leaves them out too.
//  - deletedClips: how many clips were left out because their content no longer exists
export const finalPlaylist = (ids, broadcastsById, existingContent) => {
  const lists = []
  let empty = 0
  let missing = 0
  let nested = 0
  let deletedClips = 0
  for (const id of ids || []) {
    const record = broadcastsById.get(id)
    if (!record) {
      missing++
    } else if (isCompositeBroadcast(record)) {
      nested++
    } else {
      const clips = []
      for (const contentId of record.contentList || []) {
        if (existingContent && !existingContent.has(contentId)) deletedClips++
        else clips.push({ contentId, broadcastId: id })
      }
      if (clips.length === 0) empty++
      else lists.push(clips)
    }
  }
  return { steps: compositeOrder(lists), empty, missing, nested, deletedClips }
}

// Do the players in the field already skip a broadcast without clips? Until they do, an empty
// broadcast in a composite broadcast makes the player divide by zero: it fails to synchronise and
// keeps playing its OLD list (see ARCHITECTURE.md, "Konačni raspored"). The fix is committed in
// the Android project (StudioProjects\android, branch modernize-and-stability, commit f398b8b,
// PlaylistResolver) but it is NOT in a release yet: the last one, 2.2.2 (48), is older. While
// this is `false`, the final playlist shows a warning. Set it to `true` once the devices run a
// version that has the fix (each device record has `appVersion`).
export const PLAYER_SKIPS_EMPTY_BROADCASTS = false

// Where each content item is played: Map(contentId -> [{ id, name, count }]), the regular
// broadcasts (not their multiplier copies, which only repeat the original) whose list holds
// it, `count` = how many times, sorted by name.
export const contentUsage = (broadcasts) => {
  const usage = new Map()
  for (const broadcast of broadcasts || []) {
    if (broadcast.sourceId || isCompositeBroadcast(broadcast)) continue
    const counts = new Map()
    for (const contentId of broadcast.contentList || []) {
      counts.set(contentId, (counts.get(contentId) || 0) + 1)
    }
    for (const [contentId, count] of counts) {
      if (!usage.has(contentId)) usage.set(contentId, [])
      usage.get(contentId).push({ id: broadcast.id, name: broadcast.name, count })
    }
  }
  usage.forEach(list => list.sort((a, b) => compareValues(a.name, b.name)))
  return usage
}
